import React, { useEffect, useState } from "react";
import { CREATE_USER_TASK } from "../../../../utils/GraphQL/mutations";
import { GET_USER_INFO_FOR_TASKS } from "../../../../utils/GraphQL/queries";
import "./userTaskCreate.css";
import { useLazyQuery, useMutation } from "@apollo/client";
import BdrInput from "../../../../components/BdrInput/BdrInput";
import BdrModal from "../../../../components/BdrModal/BdrModal";
import BdrButton from "../../../../components/BdrButton/BdrButton";
import AddTaskIcon from "@mui/icons-material/AddTask";
import ClientSearchInput from "../ClientSearchInput/ClientSearchInput";
import EventDateTimeInput from "../EventDateTimeInput/EventDateTimeInput";
import UserTaskFormValidator from "../../../../utils/formValidators/userTaskFormValidator";
import { useSnackbar } from "../../../../utils/GlobalSnackbar/GlobalSnackbarContext";

// One more than AdminDashboardTasks' TASK_TYPES: creating a task offers
// "F/U 2", editing one does not. Left as-is — that difference predates this
// pass and may be intentional.
const USER_TASK_TYPES = [
  "F/U",
  "F/U 2",
  "Schedule",
  "Send Payment Receipt",
  "Appt / Invoice",
  "Add To Ledger",
  "Other",
];

const clearedForm = {
  assigneeName: "All",
  assigneeId: "22",
  taskType: "",
  note: "",
  associatedClientId: "",
  associatedClientName: "",
  active: true,
  dateCreated: "",
  dueDate: "",
};

const UserTaskCreate = ({
  clientObj,
  queryTasks,
  iconOnly,
  /** shorter label where the trigger shares a line with other controls */
  compactLabel,
}) => {
  const { handleSnack } = useSnackbar();
  const [openModal, setOpenModal] = useState(false);
  const [taskForm, setTaskForm] = useState({ ...clearedForm });
  const [selectedUserIndex, setSelectedUserIndex] = useState(0);
  const [afterLastUserIndex, setAfterLastUserIndex] = useState(0);
  const [formErrors, setFormErrors] = useState([]);
  const [usersInfo, setUsersInfo] = useState([]);
  const [getUsersInfoCall] = useLazyQuery(GET_USER_INFO_FOR_TASKS);
  // `inputsWidth` used to hold "45%" for the two dropdowns' sx props and was
  // never set to anything else — the grid in CSS does it now.
  const [createNewTaskCall] = useMutation(CREATE_USER_TASK);

  // Form Validator
  const taskValidator = new UserTaskFormValidator(taskForm);

  useEffect(() => {
    if (clientObj?._id && openModal === true) {
      setTaskForm((prev) => ({
        ...prev,
        associatedClientId: clientObj?._id,
        associatedClientName: `${clientObj?.firstName} ${clientObj?.lastName}`,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientObj, openModal]);

  useEffect(() => {
    if (usersInfo?.length <= 0 && openModal) {
      makeGetInfoCall()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openModal]);

  const makeGetInfoCall = async () => {
    const { data } = await getUsersInfoCall()

    if ( data?.getUsersNamesForTasks ) {
      const index = data?.getUsersNamesForTasks?.length + 1;
      setSelectedUserIndex(index);
      setAfterLastUserIndex(index);
      setUsersInfo([...data?.getUsersNamesForTasks])
    }
  };

  const handleChangeAssignee = (index) => {
    setSelectedUserIndex(index);

    if (index !== afterLastUserIndex) {
      const selected = usersInfo?.[index];

      setTaskForm((prev) => ({
        ...prev,
        assigneeId: selected?._id || "",
        assigneeName: `${selected?.firstName} ${selected?.lastName}`,
      }));

      return;
    }

    setTaskForm((prev) => ({
      ...prev,
      assigneeId: "22",
      assigneeName: "All",
    }));
  };

  const handleChange = ({ name, value }) => {
    let dueDateTime = null;
    if (name === "dueDate") {
      const dayMill = 1000 * 60 * 60 * 24;
      dueDateTime = (dayMill * value + Date.now()).toString();
    }

    setTaskForm((prev) => ({
      ...prev,
      [name]: value,
      dueDate: dueDateTime || prev?.dueDate,
    }));
  };

  const searchResultSet = (result) => {
    setTaskForm((prev) => ({
      ...prev,
      associatedClientId: result?._id,
      associatedClientName: `${result?.firstName} ${result?.lastName}`,
    }));
  };

  const clearSearchResult = () => {
    setTaskForm((prev) => ({
      ...prev,
      associatedClientId: "",
      associatedClientName: "",
    }));
  };

  const timeSelected = (timeStr) => {
    setTaskForm((prev) => ({
      ...prev,
      dueDate: timeStr,
    }));
  };

  const closeAndReset = () => {
    setTaskForm((prev) => ({
      ...clearedForm,
    }));
    setOpenModal(false);
  };

  const createNewTask = async () => {
    const formOk = await taskValidator.formValid();

    if (formOk !== true) {
      setFormErrors([...formOk]);
      return;
    }

    if (formOk === true) {
      const { data } = await createNewTaskCall({
        variables: {
          ...taskForm,
          dateCreated: Date.now().toString(),
        },
      });

      if (data?.createUserTask) {
        if (handleSnack) {
          handleSnack("Task Created", 2500);
        }

        if (queryTasks) {
          queryTasks();
        }

        closeAndReset();
        return;
      }

      if (handleSnack) {
        handleSnack("Something went wrong...", 3000);
      }

      return;
    }
  };

  return (
    <>
      {/* `iconOnly` is for the client console's rail, where three equal-width
          icon buttons replaced a stack of full-width labelled ones. Every other
          caller still gets the labelled button and is untouched. */}
      {iconOnly ? (
        <BdrButton
          variant="action"
          icon={<AddTaskIcon />}
          iconOnly
          ariaLabel="Create Task"
          onClick={(event) => {
            event.stopPropagation();
            setOpenModal(true);
          }}
        />
      ) : (
        /*
         * `confirm`, not `confirm-solid`: the surface picks the weight, and the
         * only caller of this branch is the Tasks console header, which sits on
         * `bdr-grad-container-heavy` — a NEUTRAL container, not the saturated
         * panel gradient. Solid is for the latter.
         *
         * This was a hand-rolled <button> with its own gradient, border, shadow
         * and a typed `z-index: 22`. The z-index was the live bug: the admin
         * AppBar is sticky at `zIndex: 10` (AdminNavbar.jsx), so 22 painted the
         * trigger OVER the navbar as the page scrolled under it. A page trigger
         * needs no stacking order at all.
         */
        <BdrButton
          variant="confirm"
          icon={<AddTaskIcon />}
          onClick={(event) => {
            event.stopPropagation();
            setOpenModal(true);
          }}
        >
          {compactLabel ? "Create" : "Create Task"}
        </BdrButton>
      )}
      <BdrModal
        open={openModal}
        onClose={closeAndReset}
        title="Create Task"
        variant="panel"
        actions={
          <BdrButton
            variant="confirm-solid"
            onClick={() => {
              setFormErrors([]);
              createNewTask();
            }}
          >
            Save Task
          </BdrButton>
        }
      >
        <div id="cal-id" className="user-task-inputs-container">
        {formErrors?.length > 0 && (
          <ul className="bdr-alert bdr-alert--danger bdr-alert--start user-task-error-ul">
            {formErrors?.map((err, index) => {
              return <li key={err + index}>{err}</li>;
            })}
          </ul>
        )}
        <div className="user-task-row">
          <BdrInput
            label="Assign To"
            name="user-task-select"
            options={[
              ...(usersInfo || []).map((userInfo, index) => ({
                value: index,
                label: `${userInfo?.firstName} ${userInfo?.lastName}`.trim(),
              })),
              { value: afterLastUserIndex, label: "All" },
            ]}
            value={selectedUserIndex}
            /* parseInt: the dropdown returns a STRING (both a native
               <select> and BdrSelectPopup do), and
               handleChangeAssignee compares with !== against the
               numeric afterLastUserIndex. MUI's Select kept the
               number type. */
            onChange={(event) => {
              handleChangeAssignee(parseInt(event.target.value, 10));
            }}
          />
          <BdrInput
            label="Task Type"
            name="taskType"
            options={USER_TASK_TYPES}
            value={taskForm?.taskType}
            onChange={(event) => {
              handleChange(event.target);
            }}
          />
        </div>
        <div className="user-task-row">
          {/* Both cells are label-above-control, the same stack BdrInput
              renders, so all four labels in this modal start on the same
              left edge and sit at the same height. */}
          {!taskForm?.associatedClientId ? (
            /* No label of our own — ClientSearchInput already renders
               "Search Clients" above its box, and two labels on one control
               is one too many. */
            <div className="user-task-cell">
              <ClientSearchInput returnResults={searchResultSet} />
            </div>
          ) : (
            <div className="user-task-cell">
              <span className="bdr-field-label">Associated Client</span>
              <div className="user-task-client-row">
                <span className="user-task-client-name">
                  {taskForm?.associatedClientName}
                </span>
                <BdrButton size="sm" onClick={clearSearchResult}>
                  Remove
                </BdrButton>
              </div>
            </div>
          )}
          <div className="user-task-cell">
            <span className="bdr-field-label">Due Date</span>
            <EventDateTimeInput
              onChange={timeSelected}
              defaultTime={{ hour: 10, minute: 0, amPm: "AM" }}
              hideTime={true}
            />
          </div>
        </div>
        <BdrInput
          label="Note"
          name="note"
          value={taskForm?.note}
          onChange={(event) => handleChange(event.target)}
          multiline
          rows={3}
        />
        </div>
      </BdrModal>
    </>
  );
};

export default UserTaskCreate;
